import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { rescues } from 'src/backend';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useCancellationReasonOptions } from 'src/hooks/useCancelationReason';

export default function ConfirmModal({
  title: title_,
  confirmMessage,
  okLabel,
  cancelLabel: cancelLabel_,
  open,
  handleCancel,
  donation,
  onUnClaim,
}) {
  const { t } = useTranslation('translation');
  const title = title_ ?? t('donations.are-you-sure');
  const cancelLabel = cancelLabel_ ?? t('donations.cancel');
  const history = useHistory();
  const [selectedReason, setSelectedReason] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otherReason, setOtherReason] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const reasons = useCancellationReasonOptions({ open });

  const handleCancelDonation = async () => {
    if (loading) return;

    setLoading(true);
    let cancelled = false;

    try {
      await rescues.cancel({
        donationId: donation.id,
        reasonId: selectedReason,
        otherReason,
        shouldUpdateCalendar: true,
        isPartialRescue: donation.is_partial,
        rescueId: donation.rescue_id,
        recipientTeamId: donation.recipient_team_id,
      });
      cancelled = true;
    } catch {
      enqueueSnackbar('Something went wrong while unclaiming the donation', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }

    if (!cancelled) return;

    setOtherReason('');
    setSelectedReason(null);
    handleCancel();
    onUnClaim();
    history.goBack();
  };

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle sx={{ mb: 1 }}>{title}</DialogTitle>

      <DialogContent>
        <Typography>{confirmMessage}</Typography>
      </DialogContent>
      <Box px={5}>
        <FormControl fullWidth>
          <InputLabel id="reason-select">{t('donations.reason')}</InputLabel>
          <Select
            labelId="reason-select"
            id="reason-select"
            value={selectedReason}
            label={t('donations.reason')}
            onChange={(e) => {
              setSelectedReason(e.target.value);
            }}
          >
            {reasons.map((item) => (
              <MenuItem value={item.id} key={item.id}>
                {item.reason}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box py={2}>
          {selectedReason === 'custom' ? (
            <TextField
              id="custom-reason"
              label={t('donations.specify-reason')}
              multiline
              rows={4}
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              fullWidth
            />
          ) : (
            ''
          )}
        </Box>
      </Box>

      <DialogActions>
        <Button onClick={handleCancel}>{cancelLabel}</Button>
        <LoadingButton
          onClick={handleCancelDonation}
          variant="outlined"
          loading={loading}
          disabled={
            !selectedReason || (selectedReason === 'custom' && !otherReason)
          }
        >
          {okLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
